import { GlobalConfigType } from 'actions'
import type { InputType } from 'config'
import { accountTypes, isExternalOriginatorAccountType } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getTemplateCompanyUsers } from 'services/apis/conditions'
import { getTeams } from 'services/apis/team'

/**
 * The "who may use this template" block shared by the email and note template editors: profiles,
 * named users and teams, OR-ed together server side. All three empty means everyone, which is
 * every template that predates the feature.
 */
export const availabilityInputs = (): Record<string, InputType> => ({
  availableAccountTypes: {
    title: 'Available Profiles',
    inputType: 'multiselect',
    required: false,
    options: {},
    value: [],
    span: 'full',
    tooltip:
      'Leave empty for all profiles. Only Nexys company profiles can be selected, not brokers, third-party loan officers or processors, table funders, correspondents, or branches.',
  },
  availableUserIds: {
    title: 'Available Users',
    inputType: 'multiselect',
    required: false,
    options: {},
    value: [],
    span: 'full',
    tooltip:
      'Nexys company users who can use this template even when their profile is not selected. Brokers and other third-party originators are not listed.',
  },
  availableTeamIds: {
    title: 'Available Teams',
    inputType: 'multiselect',
    required: false,
    options: {},
    value: [],
    span: 'full',
    tooltip:
      'Teams whose members can use this template. A user matches if their profile is selected, OR they are listed under Available Users, OR they are in one of these teams. Leave all three empty to offer it to everyone.',
  },
})

/**
 * Options for those three fields. `enabled` is false in the override editors, which do not own
 * availability, so nothing is fetched there.
 */
export const useTemplateAvailabilityOptions = (enabled = true) => {
  const accountTypeConfig = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.ACCOUNT_TYPES] || {},
  )
  const [userOptions, setUserOptions] = useState<Record<string, string>>({})
  const [teamOptions, setTeamOptions] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!enabled) return
    getTemplateCompanyUsers()
      .then((users) => {
        const opts: Record<string, string> = {}
        ;(users || []).forEach((user) => {
          if (!user?.id) return
          opts[String(user.id)] = `${user.name || user.email}${user.email ? ` (${user.email})` : ''}`
        })
        setUserOptions(opts)
      })
      .catch(() => setUserOptions({}))
    getTeams()
      .then((teams) => {
        const opts: Record<string, string> = {}
        ;(teams || []).forEach((team: any) => {
          if (!team?.id) return
          opts[String(team.id)] = team.name
        })
        setTeamOptions(opts)
      })
      .catch(() => setTeamOptions({}))
  }, [enabled])

  const profileOptions = (): Record<string, string> => {
    const opts: Record<string, string> = {}
    const configKeys = Object.keys(accountTypeConfig || {})
    if (configKeys.length) {
      configKeys.forEach((key) => {
        const cfg = accountTypeConfig[key]
        if (cfg && cfg.visible === false) return
        if (isExternalOriginatorAccountType(key) || isExternalOriginatorAccountType(cfg?.baseType)) return
        opts[key] = cfg?.title || accountTypes[key] || key
      })
    } else {
      Object.keys(accountTypes).forEach((key) => {
        if (isExternalOriginatorAccountType(key)) return
        opts[key] = accountTypes[key]
      })
    }
    return opts
  }

  return { profileOptions, userOptions, teamOptions }
}

const selectedKeys = (value: any): string[] =>
  Array.isArray(value) ? value.map(String) : Object.keys(value || {}).filter((key) => value[key])

/**
 * Turn what the multiselects hold into what the API takes. Selections are only dropped once their
 * options have loaded, so saving before that cannot quietly strip a scope; the server validates
 * both id lists anyway.
 */
export const availabilityPayload = (
  data: Record<string, any>,
  options: { userOptions: Record<string, string>; teamOptions: Record<string, string> },
) => {
  const ids = (value: any, offered: Record<string, string>) => {
    const known = Object.keys(offered).length > 0
    return selectedKeys(value)
      .map((id) => Number(id))
      .filter((id) => id > 0 && (!known || !!offered[String(id)]))
  }

  return {
    availableAccountTypes: selectedKeys(data.availableAccountTypes).filter(
      (key) => !isExternalOriginatorAccountType(key),
    ),
    availableUserIds: ids(data.availableUserIds, options.userOptions),
    availableTeamIds: ids(data.availableTeamIds, options.teamOptions),
  }
}

/** The stored id lists as the multiselects want them (strings). */
export const availabilityValues = (defaults: {
  availableAccountTypes?: string[] | null
  availableUserIds?: number[] | null
  availableTeamIds?: number[] | null
}) => ({
  availableAccountTypes: defaults.availableAccountTypes || [],
  availableUserIds: (defaults.availableUserIds || []).map((id) => String(id)),
  availableTeamIds: (defaults.availableTeamIds || []).map((id) => String(id)),
})
